import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setFooterHeight, setDataTable } from '@/actions'
import { Resizable } from 're-resizable'
import DataTable from '../DataTable/DataTable'
import { getMapStyleJS } from '@/utilities/mapStyleUtils'
import useClearSelection from '@/utilities/Hooks/useClearSelection'
import { useAnalytics } from '@/contexts/AnalyticsProvider'

const DEFAULT_TABLE_HEIGHT = 320
const MINIMUM_TABLE_HEIGHT = 200

const resolveStoredHeight = (value, fallback = DEFAULT_TABLE_HEIGHT) => {
  if (value === null || value === undefined) return fallback
  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) return fallback
  return Math.max(0, numericValue)
}

export default function Footer() {
  const dispatch = useDispatch()
  const { trackEvent } = useAnalytics()
  const dataTable = useSelector(state => state.dataTable)
  const mapStyle = useSelector(state => state.mapStyle)
  const clearSelection = useClearSelection()
  const [height, setHeight] = useState(0)
  const [layoutRect, setLayoutRect] = useState({ left: 0, width: 0 })
  const [mapRect, setMapRect] = useState({ left: 0, width: 0 })

  const shouldShowDataTable = useMemo(() => {
    if (!dataTable || !dataTable.layerId) return false
    if (!mapStyle) return false

    const style = getMapStyleJS(mapStyle)
    if (!style?.layers) return false
    const currentLayer = style.layers.find(
      layer => layer.id === dataTable.layerId
    )

    return !!currentLayer
  }, [dataTable, mapStyle])

  const clampHeight = useCallback(value => {
    if (value === null || Number.isNaN(value)) {
      return MINIMUM_TABLE_HEIGHT
    }
    return Math.max(MINIMUM_TABLE_HEIGHT, value)
  }, [])

  const handleClose = useCallback(() => {
    trackEvent('map_table_toggled', { is_open: false })
    localStorage.removeItem('dataTable')
    setHeight(0)
    dispatch(setFooterHeight(0))
    dispatch(setDataTable(null))
    // Clear the cyan `selected_features` highlight that DataTable writes when
    // rows are checked. Without this, the outline persists on the map after
    // the table closes because nothing else owns the source. `clearSelection`
    // only empties `SELECTED_FEATURES.sourcesArray` — the `clicked_feature`
    // highlight from FAP/MapPopup is left intact.
    clearSelection()
  }, [dispatch, clearSelection, trackEvent])

  const handleResize = useCallback(
    (event, direction, ref) => {
      const nextHeight = clampHeight(parseFloat(ref.style.height))
      setHeight(nextHeight)
    },
    [clampHeight]
  )

  const handleResizeStop = useCallback(
    (event, direction, ref) => {
      const nextHeight = clampHeight(parseFloat(ref.style.height))
      setHeight(nextHeight)
      dispatch(setFooterHeight(nextHeight))

      if (dataTable) {
        // Remove legacy key before dispatching
        // eslint-disable-next-line no-unused-vars
        const { foooterHeight, ...cleanedDataTable } = dataTable
        const updatedDataTable = {
          ...cleanedDataTable,
          footerHeight: nextHeight,
        }

        dispatch(setDataTable(updatedDataTable))

        // Save to localStorage
        try {
          localStorage.setItem('dataTable', JSON.stringify(updatedDataTable))
        } catch (error) {
          console.warn(
            'Failed to save data table state to localStorage:',
            error
          )
        }
      }
    },
    [clampHeight, dataTable, dispatch]
  )

  useEffect(() => {
    const storedDataTable = localStorage.getItem('dataTable')
    if (!storedDataTable) return

    try {
      const parsedDataTable = JSON.parse(storedDataTable)
      if (!parsedDataTable) return

      const legacyHeight =
        parsedDataTable.footerHeight ?? parsedDataTable.foooterHeight
      const normalizedHeight = resolveStoredHeight(legacyHeight)

      // Remove legacy key to prevent infinite re-renders
      // eslint-disable-next-line no-unused-vars
      const { foooterHeight, ...cleanedDataTable } = parsedDataTable

      dispatch(
        setDataTable({
          ...cleanedDataTable,
          footerHeight: normalizedHeight,
        })
      )
    } catch (error) {
      console.warn('Failed to parse data table state from localStorage:', error)
      localStorage.removeItem('dataTable')
    }
  }, [dispatch])

  useEffect(() => {
    if (!dataTable) {
      localStorage.removeItem('dataTable')
      setHeight(0)
      dispatch(setFooterHeight(0))
      return
    }

    const candidateHeight = resolveStoredHeight(
      dataTable.footerHeight ?? dataTable.foooterHeight
    )
    const resolvedHeight = Math.max(MINIMUM_TABLE_HEIGHT, candidateHeight)

    // Only update state if height changed
    setHeight(prevHeight => {
      if (prevHeight !== resolvedHeight) {
        dispatch(setFooterHeight(resolvedHeight))
        return resolvedHeight
      }
      return prevHeight
    })

    // Only update if there's a legacy key or the height needs normalization
    if (dataTable.foooterHeight !== undefined) {
      // Remove legacy key before dispatching to prevent infinite re-renders
      // eslint-disable-next-line no-unused-vars
      const { foooterHeight, ...cleanedDataTable } = dataTable

      dispatch(
        setDataTable({
          ...cleanedDataTable,
          footerHeight: resolvedHeight,
        })
      )
    } else if (dataTable.footerHeight !== resolvedHeight) {
      // Height needs normalization
      dispatch(
        setDataTable({
          ...dataTable,
          footerHeight: resolvedHeight,
        })
      )
    }
    // If everything is already correct, do nothing (no localStorage write needed here)
    // localStorage is written only in handleResizeStop when user manually resizes
  }, [dataTable, dispatch])

  const resolvedHeight = shouldShowDataTable ? height : 0
  const isVisible = shouldShowDataTable && resolvedHeight > 0

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const updateRects = () => {
      const mainElement = document.getElementById('main')
      const mapContainer = document.getElementById('mapContainer')

      if (mainElement) {
        const rect = mainElement.getBoundingClientRect()
        setLayoutRect({ left: rect.left, width: rect.width })
      }

      if (mapContainer) {
        const rect = mapContainer.getBoundingClientRect()
        setMapRect({ left: rect.left, width: rect.width })
      }
    }

    updateRects()

    const handleWindowResize = () => updateRects()
    window.addEventListener('resize', handleWindowResize)

    const mainElement = document.getElementById('main')
    const mapContainer = document.getElementById('mapContainer')
    let observer
    let mapObserver
    if (mainElement && 'ResizeObserver' in window) {
      observer = new ResizeObserver(() => updateRects())
      observer.observe(mainElement)
    }

    if (mapContainer && 'ResizeObserver' in window) {
      mapObserver = new ResizeObserver(() => updateRects())
      mapObserver.observe(mapContainer)
    }

    return () => {
      window.removeEventListener('resize', handleWindowResize)
      if (observer) observer.disconnect()
      if (mapObserver) mapObserver.disconnect()
    }
  }, [])

  const overlayStyle = useMemo(() => {
    if (mapRect.width) {
      return {
        left: `${mapRect.left}px`,
        width: `${mapRect.width}px`,
      }
    }

    if (layoutRect.width) {
      return {
        left: `${layoutRect.left}px`,
        width: `${layoutRect.width}px`,
      }
    }

    return {
      left: 0,
      right: 0,
    }
  }, [layoutRect.left, layoutRect.width, mapRect.left, mapRect.width])

  return (
    <div
      className='pointer-events-none fixed bottom-0 left-0 right-0 z-9999'
      aria-hidden={!isVisible}
      style={overlayStyle}
    >
      <Resizable
        className={`pointer-events-auto transition-[transform,opacity] duration-200 ease-in-out ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        } border-t-2 border-slate-300 bg-slate-100 shadow-[0_-6px_24px_rgba(15,23,42,0.18)] relative`}
        enable={{ top: true }}
        size={{ width: '100%', height: resolvedHeight }}
        minWidth='100%'
        minHeight={MINIMUM_TABLE_HEIGHT}
        onResize={handleResize}
        onResizeStop={handleResizeStop}
        handleStyles={{
          top: {
            cursor: 'ns-resize',
            height: '12px',
            top: '-6px',
            width: '100%',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
        handleComponent={{
          top: (
            <div className='flex items-center justify-center w-full h-full group'>
              <div className='w-16 h-1 bg-slate-400 rounded-full group-hover:bg-blue-500 transition-colors' />
            </div>
          ),
        }}
      >
        {isVisible && (
          <DataTable height={resolvedHeight} onClose={handleClose} />
        )}
      </Resizable>
    </div>
  )
}
